import { t } from '../i18n';
/**
 * Photos, sans Firebase Storage.
 *
 * Le projet n'a pas de serveur et le forfait actuel n'ouvre pas Storage :
 * une image est donc redimensionnee ici, dans le navigateur, puis rangee
 * dans la base sous forme de data URL. Cela ne passe que parce qu'on
 * borne la taille avec severite — un avatar tient en quelques kilo-octets,
 * une photo de discussion en quelques dizaines.
 *
 * C'est un compromis assume, pas une bonne pratique : le jour ou Storage
 * s'ouvre, seul ce fichier change, puisque le reste de l'application ne
 * manipule que des URL.
 */

/**
 * Cote maximal et qualite, par usage.
 *
 * L'avatar est relu a chaque profil affiche : il reste le plus contraint
 * des trois. 256 px etaient trop justes — la photo piquait des l'ecran
 * "Personnaliser mon profil", ou elle s'affiche a 104 pt, soit plus de 300 px sur
 * un telephone recent.
 */
const PRESETS = {
  avatar: { maxSide: 448, quality: 0.85 },
  /** Photo de discussion ou de post : lue seulement a l'ouverture. */
  photo: { maxSide: 1280, quality: 0.78 },
} as const;

export type ImagePreset = keyof typeof PRESETS;

/**
 * Au-dela, on refuse : une image trop lourde ferait echouer l'ecriture
 * cote base avec une erreur incomprehensible. Les seuils portent sur la
 * data URL, environ un tiers plus longue que l'image elle-meme.
 */
const MAX_STORED_BYTES = { avatar: 260_000, photo: 900_000 } as const;

export class ImageError extends Error {}

/**
 * Lit un fichier choisi par l'utilisateur et rend une data URL JPEG
 * redimensionnee. Le format de sortie est toujours JPEG : le PNG d'une
 * capture d'ecran peserait dix fois plus pour le meme rendu.
 */
export async function readImage(file: File, preset: ImagePreset): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new ImageError(t.composants.image.pasUneImage);
  }

  const bitmap = await loadBitmap(file);
  const { maxSide, quality } = PRESETS[preset];
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));

  const context = canvas.getContext('2d');
  if (!context) throw new ImageError(t.composants.image.navigateur);
  // Fond blanc : un PNG transparent aplati en JPEG virerait au noir.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

  const dataUrl = canvas.toDataURL('image/jpeg', quality);
  if (dataUrl.length > MAX_STORED_BYTES[preset]) {
    throw new ImageError(t.composants.image.tropLourde);
  }
  return dataUrl;
}

/**
 * Cote maximal de la photo confiee au recadrage.
 *
 * Une photo d'iPhone fait 4032 px de large : la poser telle quelle dans
 * la vue tiendrait quarante mega-octets de bitmap pour un disque de 300
 * px de diametre. Seize cents suffisent — meme le cadrage le plus serre
 * y trouve encore plus de pixels que l'avatar n'en garde.
 */
const SOURCE_MAX_SIDE = 1600;

/** Une photo decodee, redressee et bornee, prete a etre cadree. */
export interface SourceARecadrer {
  /** A poser dans un `<img>`, et a rendre avec `oublier()`. */
  url: string;
  width: number;
  height: number;
  oublier: () => void;
}

/**
 * Prepare la photo que le recadrage va montrer.
 *
 * Le detour par un JPEG intermediaire n'est pas un gaspillage : il fait
 * d'une pierre trois coups. L'orientation EXIF est appliquee une fois
 * pour toutes — donc la vue et le decoupage voient la meme image, la ou
 * un `<img>` nourri du fichier d'origine risquait de la coucher. La
 * definition est bornee, donc le geste reste fluide. Et le decoupage
 * final redessine une source sans metadonnee, ou un pixel est un pixel.
 */
export async function prepareSource(file: File): Promise<SourceARecadrer> {
  if (!file.type.startsWith('image/')) {
    throw new ImageError(t.composants.image.pasUneImage);
  }

  const bitmap = await loadBitmap(file);
  const scale = Math.min(1, SOURCE_MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));

  const context = canvas.getContext('2d');
  if (!context) throw new ImageError(t.composants.image.navigateur);
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  // Le decode d'origine ne sert plus : sur un telephone, ces douze
  // millions de pixels valent la peine d'etre rendus tout de suite.
  if ('close' in bitmap) bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((sortie) => resolve(sortie), 'image/jpeg', 0.92);
  });
  if (!blob) throw new ImageError(t.composants.image.pasOuverte);

  const url = URL.createObjectURL(blob);
  return {
    url,
    width: canvas.width,
    height: canvas.height,
    oublier: () => URL.revokeObjectURL(url),
  };
}

/**
 * Decoupe un carre de la source et rend une data URL, comme `readImage`.
 *
 * La zone est donnee dans les pixels de la source. Le disque montre a
 * l'ecran n'est qu'un viseur : ce qui part est le carre qui l'inscrit,
 * puisque c'est `Avatar` qui arrondit, partout et de lui-meme.
 *
 * On ne sort jamais plus grand que ce qu'on a decoupe : agrandir un
 * cadrage serre n'inventerait aucun detail et ne ferait qu'alourdir.
 */
export function readCrop(
  source: CanvasImageSource,
  zone: { x: number; y: number; cote: number },
  preset: ImagePreset,
): string {
  const { maxSide, quality } = PRESETS[preset];
  const cote = Math.max(1, Math.round(Math.min(maxSide, zone.cote)));

  const canvas = document.createElement('canvas');
  canvas.width = cote;
  canvas.height = cote;

  const context = canvas.getContext('2d');
  if (!context) throw new ImageError(t.composants.image.navigateur);
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, cote, cote);
  context.drawImage(source, zone.x, zone.y, zone.cote, zone.cote, 0, 0, cote, cote);

  const dataUrl = canvas.toDataURL('image/jpeg', quality);
  if (dataUrl.length > MAX_STORED_BYTES[preset]) {
    throw new ImageError(t.composants.image.tropLourde);
  }
  return dataUrl;
}

/**
 * Decode le fichier.
 * `createImageBitmap` fait le travail partout ou il existe et applique
 * l'orientation EXIF ; le repli par `<img>` couvre les navigateurs plus
 * anciens, au prix d'une photo de telephone parfois couchee.
 */
async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Option non supportee : on retombe sur le chemin classique.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new ImageError(t.composants.image.pasOuverte));
      image.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}
